import { useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Network, ZoomIn, ZoomOut, RotateCcw, Search, X, Store } from "lucide-react";
import { getEmployeeTree } from "../../api/employees";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Card, Skeleton, EmptyState, Avatar, Input, Select } from "../../components/ui";

// Below this width a side-by-side org chart cannot fit no matter how far it is
// scaled down — shrinking far enough to fit a phone makes the names unreadable.
// Under it the same tree is drawn as an indented vertical list instead, which
// is width-constrained by construction and so can never overflow sideways.
const COMPACT_BREAKPOINT = "(max-width: 767px)";

function useIsCompact() {
  const [isCompact, setIsCompact] = useState(
    () => typeof window !== "undefined" && window.matchMedia(COMPACT_BREAKPOINT).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(COMPACT_BREAKPOINT);
    const onChange = (event) => setIsCompact(event.matches);
    setIsCompact(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return isCompact;
}

// An employee can report to several managers, so the API's tree repeats them
// — and their whole subtree — under each one. Draw every person exactly once,
// at their shallowest position (breadth-first). Nesting can only express one
// manager per person, so the edges that lose out come back as `links`, which
// the overlay below draws as connectors between the two cards.
function dedupeTree(roots) {
  const kept = new Map(); // employee id -> the single copy we render
  const drawnUnder = new Map(); // employee id -> id of the manager they hang from
  const links = []; // { managerId, employeeId } edges the nesting can't show
  const seenLinks = new Set();
  const result = [];
  let level = [];

  for (const node of roots || []) {
    if (kept.has(node._id)) continue;
    const copy = { ...node, reportees: [] };
    kept.set(node._id, copy);
    result.push(copy);
    level.push([node, copy]);
  }

  while (level.length > 0) {
    const next = [];
    for (const [source, copy] of level) {
      for (const child of source.reportees || []) {
        if (kept.has(child._id)) {
          // Already drawn under someone else — record the edge rather than
          // repeating the whole subtree under this manager too.
          const key = `${copy._id}>${child._id}`;
          if (drawnUnder.get(child._id) !== copy._id && !seenLinks.has(key)) {
            seenLinks.add(key);
            links.push({ managerId: copy._id, employeeId: child._id });
          }
          continue;
        }
        const childCopy = { ...child, reportees: [] };
        kept.set(child._id, childCopy);
        drawnUnder.set(child._id, copy._id);
        copy.reportees.push(childCopy);
        next.push([child, childCopy]);
      }
    }
    level = next;
  }

  return { roots: result, links, parentOf: drawnUnder };
}

// Both tiers reach this page. A branch always sees its own hierarchy — the
// token decides, and the `partner` query param is ignored for it. A chain
// picks which of its branches to look at (or its own head office) from the
// selector in the header, which maps to `?partner=<branchId>` on the tree
// endpoint. Either way one location's hierarchy is drawn at a time.
//
// The chart is never drawn larger than this, and shrinks below it as far as
// MIN_ZOOM whenever that is what it takes to fit the width without scrolling.
const DEFAULT_ZOOM = 0.8;
const MIN_ZOOM = 0.4;
const MAX_ZOOM = 1.5;

export default function EmployeeTree() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const [searchParams, setSearchParams] = useSearchParams();
  // Which location's hierarchy is on screen. "" = the logged-in partner itself
  // (a branch's own staff, or a chain's head office). Chain only.
  const [branchId, setBranchId] = useState(() =>
    isChain ? searchParams.get("partner") || "" : ""
  );
  const [branches, setBranches] = useState([]);
  const [roots, setRoots] = useState([]);
  const [filteredRoots, setFilteredRoots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [links, setLinks] = useState([]);
  const [parentOf, setParentOf] = useState(new Map());
  const [linkPaths, setLinkPaths] = useState([]);
  // Stays on until the zoom buttons are used, so the chart keeps fitting
  // itself on resize and on expand/collapse. Reset hands control back.
  const [autoFit, setAutoFit] = useState(true);
  const treeRef = useRef(null);
  const canvasRef = useRef(null);
  const rowRef = useRef(null);
  const isCompact = useIsCompact();
  // The vertical list already fits, so it is always drawn at full size.
  const effectiveZoom = isCompact ? 1 : zoom;
  // Card element for every rendered node, so the overlay can measure the two
  // ends of a cross-branch reporting line.
  const nodeEls = useRef(new Map());

  const registerNode = useCallback((id, el) => {
    if (el) nodeEls.current.set(id, el);
    else nodeEls.current.delete(id);
  }, []);

  // A manager whose reportee hangs off another branch still manages them, so
  // the reportee-count badge has to include their linked edges.
  const linkedCounts = useMemo(() => {
    const counts = new Map();
    for (const { managerId } of links) {
      counts.set(managerId, (counts.get(managerId) || 0) + 1);
    }
    return counts;
  }, [links]);

  // A chain needs its outlet list to populate the selector.
  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((res) => setBranches(res.data?.items || []))
      .catch(() => setBranches([]));
  }, [isChain]);

  // Keep ?partner= in the URL so a chain can deep-link or refresh onto the
  // same branch's chart — and so "View tree" from the employee list lands on
  // the branch that was being filtered there.
  useEffect(() => {
    if (!isChain) return;
    const next = new URLSearchParams(searchParams);
    if (branchId) next.set("partner", branchId);
    else next.delete("partner");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, isChain]);

  useEffect(() => {
    loadTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredRoots(roots);
      return;
    }

    const filterNodes = (nodes, query) => {
      const result = [];
      for (const node of nodes) {
        const matches = node.name.toLowerCase().includes(query.toLowerCase()) ||
                       (node.designation && node.designation.toLowerCase().includes(query.toLowerCase()));
        const children = filterNodes(node.reportees || [], query);
        if (matches || children.length > 0) {
          result.push({
            ...node,
            reportees: children,
          });
        }
      }
      return result;
    };

    setFilteredRoots(filterNodes(roots, searchQuery));
  }, [searchQuery, roots]);

  const loadTree = async () => {
    setLoading(true);
    try {
      const res = await getEmployeeTree(isChain && branchId ? branchId : undefined);
      const tree = dedupeTree(res.data);
      setRoots(tree.roots);
      setFilteredRoots(tree.roots);
      setLinks(tree.links);
      setParentOf(tree.parentOf);
      // Auto-expand first level
      const firstLevel = new Set();
      tree.roots.forEach(root => firstLevel.add(root._id));
      setExpandedNodes(firstLevel);
    } catch {
      setRoots([]);
      setFilteredRoots([]);
      setLinks([]);
      setParentOf(new Map());
    } finally {
      setLoading(false);
    }
  };

  // Scale the chart down until it fits the card, so a wide hierarchy never
  // produces a horizontal scrollbar. Capped at DEFAULT_ZOOM so a small org
  // is not blown up to fill the space.
  useLayoutEffect(() => {
    if (!autoFit || isCompact) return undefined;

    const fitToWidth = () => {
      const viewport = treeRef.current;
      const row = rowRef.current;
      const canvas = canvasRef.current;
      if (!viewport || !row || !canvas || row.children.length === 0) return;

      // Read the padding rather than hardcoding it — it varies by breakpoint.
      const style = window.getComputedStyle(canvas);
      const paddingX = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);

      // The row is stretched to the canvas by its min-width, so its own box
      // says nothing about how wide the chart actually is. The roots are
      // centred in it, so the span from the first to the last is the real
      // content width. Dividing by the current zoom recovers the natural
      // width, which keeps this measurement independent of the zoom it is
      // about to set so the two cannot feed each other.
      const first = row.children[0].getBoundingClientRect();
      const last = row.children[row.children.length - 1].getBoundingClientRect();
      const natural = (last.right - first.left) / (zoom || 1) + paddingX;
      if (natural <= 0) return;

      // Floor to whole percents and keep a couple of pixels in hand, so
      // rounding can never leave the content a hair too wide.
      const available = viewport.clientWidth - 2;
      const ratio = Math.floor((available / natural) * 100) / 100;
      const next = Math.min(DEFAULT_ZOOM, Math.max(MIN_ZOOM, ratio));

      setZoom((current) => (Math.abs(current - next) < 0.005 ? current : next));
    };

    fitToWidth();

    const observer = new ResizeObserver(fitToWidth);
    if (treeRef.current) observer.observe(treeRef.current);
    window.addEventListener("resize", fitToWidth);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", fitToWidth);
    };
  }, [autoFit, isCompact, filteredRoots, expandedNodes, loading, zoom]);

  // Cross-branch lines are absolutely positioned, so they have to be measured
  // from the laid-out DOM and redrawn whenever the layout can shift.
  useLayoutEffect(() => {
    const measure = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const base = canvas.getBoundingClientRect();
      // Both the cards and the overlay sit inside the zoomed container, so
      // dividing the measured gap by the zoom puts it back in SVG units.
      const scale = effectiveZoom || 1;
      const next = [];

      for (const { managerId, employeeId } of links) {
        const fromEl = nodeEls.current.get(managerId);
        const toEl = nodeEls.current.get(employeeId);
        // Either end can be collapsed away or filtered out by the search.
        if (!fromEl || !toEl) continue;

        const from = fromEl.getBoundingClientRect();
        const to = toEl.getBoundingClientRect();
        const localX = (v) => Math.round((v - base.left) / scale) + 0.5;
        const localY = (v) => Math.round((v - base.top) / scale);

        // In the vertical list, rows sit above one another, so a line between
        // their centres would cut straight across the cards. Route it out of
        // the left edge and down the gutter instead. The wide chart keeps the
        // top-to-bottom routing that matches its nested connectors.
        const d = isCompact
          ? (() => {
              const x1 = localX(from.left);
              const y1 = localY(from.top + from.height / 2);
              const x2 = localX(to.left);
              const y2 = localY(to.top + to.height / 2);
              const bow = 18;
              return `M ${x1} ${y1} C ${x1 - bow} ${y1}, ${x2 - bow} ${y2}, ${x2} ${y2}`;
            })()
          : (() => {
              const x1 = localX(from.left + from.width / 2);
              const y1 = localY(from.bottom);
              const x2 = localX(to.left + to.width / 2);
              const y2 = localY(to.top);
              const bow = Math.max(28, Math.abs(y2 - y1) / 2);
              return `M ${x1} ${y1} C ${x1} ${y1 + bow}, ${x2} ${y2 - bow}, ${x2} ${y2}`;
            })();

        next.push({ key: `${managerId}-${employeeId}`, d });
      }

      setLinkPaths(next);
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (canvasRef.current) observer.observe(canvasRef.current);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [links, filteredRoots, expandedNodes, effectiveZoom, isCompact, loading]);

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
        return newSet;
      }
      newSet.add(nodeId);
      // A reportee drawn under a different manager only exists in the DOM
      // while that manager's branch is open, so opening a node has to open
      // the path down to those reportees as well — otherwise expanding a
      // manager shows only some of the people who report to them.
      for (const { managerId, employeeId } of links) {
        if (managerId !== nodeId) continue;
        const climbed = new Set();
        let ancestor = parentOf.get(employeeId);
        while (ancestor && !climbed.has(ancestor)) {
          climbed.add(ancestor);
          newSet.add(ancestor);
          ancestor = parentOf.get(ancestor);
        }
      }
      return newSet;
    });
  };

  const handleResetView = () => {
    setAutoFit(true);
    if (treeRef.current) {
      treeRef.current.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <PageHeader
          title="Organization"
          subtitle={
            isChain
              ? "Reporting hierarchy, one location at a time — pick a branch or view your head office."
              : "The reporting hierarchy across your entire staff."
          }
          actions={
            isChain && (
              <div className="flex items-center gap-2">
                <Store size={15} className="shrink-0 text-gray-400" />
                <Select
                  value={branchId}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="w-full sm:w-64 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                >
                  <option value="">Head office (chain)</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </div>
            )
          }
        />

        <Card className="relative p-4 sm:p-6 bg-white border border-emerald-200 shadow-xl shadow-emerald-100/50">
          {/* Controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 mb-4">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-8 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 w-full text-sm"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex items-center justify-end gap-2">
              {/* The vertical list is always full size, so zoom would do
                  nothing there. */}
              <div className="hidden md:flex items-center gap-1 rounded-md border border-emerald-200/60 bg-white/90 p-1 shadow-sm">
                <button
                  onClick={() => {
                    setAutoFit(false);
                    setZoom((z) => Math.max(MIN_ZOOM, +(z - 0.1).toFixed(2)));
                  }}
                  className="rounded p-1.5 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-200"
                  title="Zoom Out"
                >
                  <ZoomOut size={15} />
                </button>
                <span className="text-xs font-medium text-gray-600 min-w-[36px] text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  onClick={() => {
                    setAutoFit(false);
                    setZoom((z) => Math.min(MAX_ZOOM, +(z + 0.1).toFixed(2)));
                  }}
                  className="rounded p-1.5 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-200"
                  title="Zoom In"
                >
                  <ZoomIn size={15} />
                </button>
              </div>
              <button
                onClick={handleResetView}
                className="hidden md:block rounded-md p-1.5 text-gray-500 hover:bg-emerald-50 hover:text-emerald-700 transition-colors duration-200"
                title="Reset View"
              >
                <RotateCcw size={15} />
              </button>
            </div>
          </div>

          {/* Tree Content */}
          {loading ? (
            <div className="flex flex-col items-center gap-4 py-10">
              <div className="animate-pulse flex flex-col items-center gap-4">
                <Skeleton className="h-16 w-48 rounded-md" />
                <div className="flex flex-wrap justify-center gap-4">
                  <Skeleton className="h-14 w-36 rounded-md" />
                  <Skeleton className="h-14 w-36 rounded-md" />
                  <Skeleton className="h-14 w-36 rounded-md" />
                </div>
              </div>
            </div>
          ) : filteredRoots.length === 0 ? (
            <EmptyState
              icon={searchQuery ? Search : Network}
              title={searchQuery ? "No matching employees found" : "No organization chart yet"}
              description={searchQuery 
                ? "Try adjusting your search terms" 
                : "Add employees and assign reporting managers to see your hierarchy here."
              }
            />
          ) : (
            <div ref={treeRef} className="overflow-auto py-4 max-h-[70vh]">
              {/* Zoom belongs on the content, not on this scroll container —
                  `zoom` scales an element's own box, so zooming the container
                  would push it wider than the card and overflow the page.
                  Wide screens get w-max so a big chart can be measured and
                  scaled to fit; compact screens get w-full, because w-max
                  would size to the untruncated width of the longest name.
                  minWidth is divided by the zoom so it still resolves to
                  exactly one container width once scaled. */}
              <div
                ref={canvasRef}
                className={`relative py-3 ${
                  isCompact ? "w-full pl-6 pr-1" : "w-max px-4 sm:px-6"
                }`}
                style={
                  isCompact
                    ? undefined
                    : { zoom, minWidth: `${100 / (zoom || 1)}%` }
                }
              >
                {/* Reporting lines that cross branches. Behind the cards, and
                    never intercepting their clicks. */}
                <svg
                  className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
                  aria-hidden="true"
                >
                  {linkPaths.map((path) => (
                    <path
                      key={path.key}
                      d={path.d}
                      fill="none"
                      stroke="#34d399"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      shapeRendering="geometricPrecision"
                    />
                  ))}
                </svg>
                <div
                  ref={rowRef}
                  className={
                    isCompact
                      ? "relative flex w-full flex-col gap-2"
                      : "relative flex flex-nowrap items-start justify-center gap-6 lg:gap-8 xl:gap-10"
                  }
                >
                  {filteredRoots.map((root) =>
                    isCompact ? (
                      <CompactTreeNode
                        key={root._id}
                        node={root}
                        isRoot
                        expandedNodes={expandedNodes}
                        onToggle={toggleNode}
                        registerNode={registerNode}
                        linkedCounts={linkedCounts}
                      />
                    ) : (
                      <TreeNode
                        key={root._id}
                        node={root}
                        isRoot
                        expandedNodes={expandedNodes}
                        onToggle={toggleNode}
                        registerNode={registerNode}
                        linkedCounts={linkedCounts}
                      />
                    ),
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Legend */}
        {!loading && roots.length > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-gradient-to-br from-emerald-500 to-green-600" />
              <span>Top Manager</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-emerald-100 border border-emerald-200" />
              <span>Employee</span>
            </div>
            <span className="text-emerald-300">|</span>
            <span>Click on any node to view details</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Narrow-screen layout: an indented vertical list. Every row is bounded by the
// container width and every label truncates, so this can never overflow
// sideways no matter how deep or how wide the hierarchy gets.
function CompactTreeNode({ node, isRoot, expandedNodes, onToggle, registerNode, linkedCounts }) {
  const hasChildren = node.reportees?.length > 0;
  const isExpanded = expandedNodes.has(node._id);
  const reporteeCount = (node.reportees?.length || 0) + (linkedCounts.get(node._id) || 0);

  const cardRef = useCallback(
    (el) => registerNode(node._id, el),
    [registerNode, node._id],
  );

  return (
    <div className="flex w-full min-w-0 flex-col">
      <div className="flex w-full min-w-0 items-center gap-1">
        <button
          onClick={() => onToggle(node._id)}
          className={`shrink-0 w-5 text-xs text-emerald-500 hover:text-emerald-700 transition-colors duration-200 ${
            reporteeCount > 0 ? "" : "invisible"
          }`}
          aria-label={isExpanded ? "Collapse" : "Expand"}
        >
          {isExpanded ? '▼' : '▶'}
        </button>
        <Link to={`/employees/${node._id}`} className="min-w-0 flex-1">
          <div
            ref={cardRef}
            className={`flex min-w-0 items-center gap-2.5 rounded-md border px-2.5 py-2 transition-colors duration-200 ${
              isRoot
                ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50"
                : "border-emerald-300 bg-white"
            }`}
          >
            <Avatar
              name={node.name}
              size={30}
              className={`shrink-0 text-xs ${
                isRoot
                  ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white'
                  : 'bg-emerald-100 text-emerald-700'
              }`}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-gray-800">{node.name}</p>
              <p className="truncate text-[11px] text-gray-500">{node.designation || "—"}</p>
            </div>
            {reporteeCount > 0 && (
              <span className="shrink-0 rounded-full border border-emerald-400 bg-white px-1.5 text-[10px] font-bold text-emerald-600">
                {reporteeCount}
              </span>
            )}
          </div>
        </Link>
      </div>

      {isExpanded && hasChildren && (
        <div className="mt-2 flex flex-col gap-2 border-l border-emerald-400 pl-3 ml-2.5">
          {node.reportees.map((child) => (
            <CompactTreeNode
              key={child._id}
              node={child}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              registerNode={registerNode}
              linkedCounts={linkedCounts}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TreeNode({ node, isRoot, expandedNodes, onToggle, registerNode, linkedCounts }) {
  const hasChildren = node.reportees?.length > 0;
  const isExpanded = expandedNodes.has(node._id);
  // Reportees drawn beneath this node, plus any reached by a cross-branch line.
  const reporteeCount = (node.reportees?.length || 0) + (linkedCounts.get(node._id) || 0);

  const cardRef = useCallback(
    (el) => registerNode(node._id, el),
    [registerNode, node._id],
  );

  return (
    <div className="flex flex-col items-center">
      <div className="relative group">
        <Link to={`/employees/${node._id}`}>
          <div
            ref={cardRef}
            className={`flex w-40 lg:w-48 xl:w-56 flex-col items-center gap-1.5 sm:gap-2 rounded-lg sm:rounded-md border px-3 sm:px-4 py-3 sm:py-4 text-center transition-all duration-300 cursor-pointer ${
              isRoot
                ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-green-50 shadow-sm hover:shadow-lg hover:shadow-emerald-500/20 hover:scale-105"
                : "border-emerald-300 bg-white shadow-sm hover:shadow-lg hover:shadow-emerald-200/30 hover:scale-105"
            }`}
          >
            <Avatar 
              name={node.name} 
              size={36} 
              className={`text-sm ${
                isRoot 
                  ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-lg shadow-emerald-500/20' 
                  : 'bg-emerald-100 text-emerald-700'
              }`}
            />
            <div>
              <p className="text-xs sm:text-body-sm font-semibold text-gray-800 group-hover:text-emerald-700 transition-colors line-clamp-1">
                {node.name}
              </p>
              <p className="text-xs text-gray-500 line-clamp-1">{node.designation || "—"}</p>
            </div>
            {reporteeCount > 0 && (
              <div className="absolute -right-1.5 -top-1.5 bg-white rounded-full p-0.5 shadow-sm border border-emerald-400">
                <span className="text-[8px] sm:text-[10px] font-bold text-emerald-600 px-1 sm:px-1.5">
                  {reporteeCount}
                </span>
              </div>
            )}
          </div>
        </Link>
      </div>

      {reporteeCount > 0 && (
        <>
          <button
            onClick={() => onToggle(node._id)}
            className="mt-1 text-emerald-500 hover:text-emerald-700 transition-colors duration-200"
          >
            {isExpanded ? '▼' : '▶'}
          </button>
          {isExpanded && hasChildren && (
            <>
              <div className="h-4 sm:h-6 w-px bg-emerald-400" />
              <div className="flex flex-nowrap items-start justify-center gap-4 lg:gap-6 xl:gap-8">
                {node.reportees.map((child) => (
                  <div key={child._id} className="relative flex flex-col items-center">
                    {node.reportees.length > 1 && (
                      <div className="absolute -top-4 sm:-top-6 h-px w-full bg-emerald-400" />
                    )}
                    <div className="h-4 sm:h-6 w-px bg-emerald-400" />
                    <TreeNode
                      node={child}
                      expandedNodes={expandedNodes}
                      onToggle={onToggle}
                      registerNode={registerNode}
                      linkedCounts={linkedCounts}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
